import { useRef, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { CreateExcelTemplate } from "services/models";
import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  Droppable,
  DroppableProvided,
  DropResult,
} from "react-beautiful-dnd";
import CreateTemplatePreview from "./CreateTemplatePreview";
import { sort } from "@utils/index";
import { Close } from "@mui/icons-material";
import useDeepCompareEffect from "shared/hook/useDeepCompareEffect";

interface IDateFormatInfoDialog {
  value?: Array<string>;
  open: boolean;
  onClose: () => void;
  htmlData: Array<CreateExcelTemplate>;
  tableRowsHeight: Array<string>;
  onSave?: (value: Array<string>) => void;
  coordinate?: string;
}

const ColumnRangeDialog = ({
  value = [],
  open,
  onClose,
  htmlData,
  tableRowsHeight,
  onSave = () => {},
  coordinate = "",
}: IDateFormatInfoDialog) => {
  const [data, setData] = useState<Array<string>>([]);
  const previewRef = useRef<any>();

  useDeepCompareEffect(() => {
    if (open) {
      setData([...value]);
    }
  }, [open, value]);

  const handleCancel = () => {
    onClose();
  };

  const handleOK = () => {
    onSave(data);
    onClose();
  };

  const handleDnd = (result: DropResult) => {
    if (
      !result.destination ||
      result.destination.index === result.source.index
    ) {
      return;
    }
    const sorted = sort(data, result.source.index, result.destination.index);
    setData(sorted);
  };

  const handleDelete = (index: number) => {
    let newData = [...data];
    newData.splice(index, 1);
    setData([...newData]);
    if (previewRef && previewRef.current) {
      previewRef.current.setSelectedCoordinate(newData);
    }
  };

  return (
    <Dialog
      maxWidth="xl"
      sx={{
        "& .MuiDialog-paper": { width: "100%", height: "100%" },
        zIndex: 1300,
      }}
      open={open}
    >
      <DialogTitle>
        入力範囲設定
        <Typography component={"span"} variant="subtitle1" sx={{ pl: 1 }}>
          入力範囲をドラッグで選択してください
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack direction={{ xs: "column", md: "row" }} pt={0.5} height={"100%"}>
          <Card sx={{ height: "100%", width: 250 }}>
            <CardContent sx={{ height: "100%", maxHeight: "100%" }}>
              <DragDropContext onDragEnd={handleDnd}>
                <div style={{ maxHeight: "100%", overflow: "auto" }}>
                  <Droppable droppableId="dates">
                    {(provided: DroppableProvided) => (
                      <List
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        sx={{
                          height: "100%",
                          display: open ? "inherit" : "none",
                        }}
                      >
                        {data.map((item, index) => (
                          <Draggable
                            key={index}
                            draggableId={"coordinate-" + index}
                            index={index}
                          >
                            {(provided: DraggableProvided) => (
                              <ListItem
                                sx={{ bgcolor: "white" }}
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                secondaryAction={
                                  <IconButton
                                    edge="end"
                                    onClick={() => handleDelete(index)}
                                  >
                                    <Close />
                                  </IconButton>
                                }
                              >
                                <ListItemIcon sx={{ maxWidth: 20, width: 20 }}>
                                  {index + 1}
                                </ListItemIcon>
                                <ListItemText
                                  primary={item}
                                  sx={{ wordBreak: "break-all" }}
                                />
                              </ListItem>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </List>
                    )}
                  </Droppable>
                </div>
              </DragDropContext>
            </CardContent>
          </Card>
          <Card
            sx={{
              height: "100%",
              width: 300,
              minWidth: 300,
              display: "flex",
              flexDirection: "column",
              flexGrow: 1,
            }}
          >
            <CardContent sx={{ flexGrow: 1, overflow: "auto" }}>
              <CreateTemplatePreview
                ref={previewRef}
                data={htmlData}
                tableRowsHeight={tableRowsHeight}
                coordinate={coordinate}
                handleClick={() => {}}
                draggable
                handleDrag={(value) => setData([...value])}
                selectedCoordinate={open ? value : undefined}
              />
            </CardContent>
          </Card>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "center" }}>
        <Button onClick={handleCancel} variant="outlined">
          キャンセル
        </Button>
        <Button onClick={handleOK}>OK</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ColumnRangeDialog;
